import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Paper,
  Typography,
  useTheme,
} from '@mui/material'
import Icon from 'icons/Icons'
import i18n from 'lang/idiomas'
import { useEffect, useState } from 'react'

export const themeColorKeys = [
  'primary',
  'secondary',
  'success',
  'error',
  'warning',
  'info',
  'inherit',
]

export const resolveColor = (color, theme) => {
  if (!color) return theme.palette.primary.main
  if (themeColorKeys.includes(color)) {
    return color === 'inherit' ? 'inherit' : theme.palette[color].main
  }
  return color
}

const paperSxFor = (appearance) => {
  switch (appearance) {
    case 'glass':
      return {
        background: 'rgba(255,255,255,0.18)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        border: '1px solid rgba(255,255,255,0.35)',
        borderRadius: '20px',
        padding: '32px 40px',
        color: '#fff',
        boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
      }
    case 'gradient-ring':
      return {
        background: '#ffffff',
        borderRadius: '14px',
        padding: '32px 40px',
      }
    case 'clean':
    default:
      return {
        background: '#ffffff',
        borderRadius: '16px',
        padding: '32px 40px',
        boxShadow: '0 12px 32px rgba(0,0,0,0.22)',
      }
  }
}

const backdropSxFor = (appearance) => {
  const base = {
    zIndex: (theme) => theme.zIndex.drawer + 1,
    flexDirection: 'column',
    gap: '20px',
  }
  if (appearance === 'glass') {
    return {
      ...base,
      background: 'rgba(46, 75, 132, 0.45)',
      backdropFilter: 'blur(2px)',
    }
  }
  return base
}

const ringWrapperSx = {
  '@keyframes customBackdropRingSpin': {
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' },
  },
  padding: '3px',
  borderRadius: '17px',
  background: 'conic-gradient(from 0deg, #2E4B84, #426BBA, #FFA620, #62A7AF, #2E4B84)',
  display: 'inline-block',
  animation: 'customBackdropRingSpin 3s linear infinite',
}

/**
 * Overlay corporativo del Portal de Empleados con tres variantes
 * (loading / success / error) y tres apariencias visuales
 * (clean / glass / gradient-ring).
 *
 * Por defecto muestra un `CircularProgress` centrado en un `Paper` con
 * sombra suave. Si se pasa `text`, se renderiza un `Typography` debajo.
 * Para casos más complejos puede usarse `content` con JSX libre.
 *
 * @component
 *
 * @param {Object} props
 * @param {boolean} [props.open=false] - Controla la visibilidad.
 * @param {'loading'|'success'|'error'} [props.variant='loading'] - `loading` muestra spinner; `success`/`error` muestran iconos.
 * @param {string} [props.text] - Mensaje bajo el spinner/icono.
 * @param {React.ReactNode} [props.content] - JSX libre. Si se pasa, gana sobre `text`.
 * @param {string} [props.color='primary'] - Acepta claves del theme (primary, secondary, success, error, warning, info, inherit) o cualquier valor CSS válido (#hex, rgb(...)).
 * @param {'clean'|'glass'|'gradient-ring'} [props.appearance='clean'] - Estilo visual del Paper interior.
 * @param {import('@mui/material').SxProps} [props.sx] - Override del sx del Backdrop exterior.
 * @param {import('@mui/material').SxProps} [props.paperSx] - Override del sx del Paper interior.
 * @param {number} [props.autoclose] - Cierra automáticamente tras N ms y muestra `LinearProgress` de cuenta atrás.
 * @param {boolean} [props.closable=false] - Muestra un botón "Cerrar".
 * @param {() => void} [props.onHide] - Callback al cerrarse (autoclose o botón).
 * @returns {JSX.Element}
 */
const CustomBackdrop = ({
  open = false,
  variant = 'loading',
  text,
  content,
  color = 'primary',
  appearance = 'clean',
  sx = {},
  paperSx = {},
  autoclose,
  closable = false,
  onHide = () => {},
}) => {
  const theme = useTheme()
  const [internalOpen, setInternalOpen] = useState(open)
  const [progress, setProgress] = useState(100)

  useEffect(() => {
    setInternalOpen(open)
    let timer
    let interval
    if (open && autoclose) {
      setProgress(100)
      timer = setTimeout(() => {
        setInternalOpen(false)
        clearInterval(interval)
        onHide()
      }, autoclose)
      interval = setInterval(() => {
        setProgress((prev) => {
          if (prev <= 0) {
            clearInterval(interval)
            return 0
          }
          const diff = 100 / (autoclose / 250)
          return Math.max(prev - diff, 0)
        })
      }, 250)
    }
    return () => {
      clearTimeout(timer)
      clearInterval(interval)
    }
  }, [open, autoclose, onHide])

  const handleClose = () => {
    setInternalOpen(false)
    onHide()
  }

  const resolvedColor = resolveColor(color, theme)

  const inner = (
    <>
      {variant === 'loading' && <CircularProgress sx={{ color: resolvedColor }} />}
      {variant === 'success' && <Icon icon="fi-rr-check-circle" color="success.main" size="48px" />}
      {variant === 'error' && <Icon icon="fi-rr-cross-circle" color="error.main" size="48px" />}
      {content ? (
        content
      ) : text ? (
        <Typography sx={{ color: resolvedColor, fontWeight: 500, textAlign: 'center' }}>
          {text}
        </Typography>
      ) : null}
      {autoclose ? (
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ width: '100%', minWidth: 200 }}
        />
      ) : null}
      {closable ? (
        <Button variant="contained" color="primary" size="small" onClick={handleClose}>
          {i18n.t('cerrar', { defaultValue: 'Cerrar' })}
        </Button>
      ) : null}
    </>
  )

  const paperEl = (
    <Paper
      elevation={0}
      sx={{
        ...paperSxFor(appearance),
        minWidth: 260,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '20px',
        ...paperSx,
      }}
    >
      {inner}
    </Paper>
  )

  return (
    <Backdrop
      sx={{
        ...backdropSxFor(appearance),
        ...sx,
      }}
      open={internalOpen}
    >
      {appearance === 'gradient-ring' ? (
        <Box sx={ringWrapperSx}>
          <Box sx={{ background: '#ffffff', borderRadius: '14px' }}>{paperEl}</Box>
        </Box>
      ) : (
        paperEl
      )}
    </Backdrop>
  )
}

export default CustomBackdrop
